<script>
import i18n from "@/plugins/i18n";
import { mapActions } from "vuex";

import NotificationCount from '../../components/NotificationCount.vue';
import SustLinks from "./notifications/SustLinks.vue";
import Other from "./notifications/Other.vue";

export default {
    components: {
        NotificationCount,
        SustLinks,
        Other,
    },

    data() {
        return {
            notificationsOpen: false,

            linkNotificationsOpen: false,
            systemNotificationsOpen: false,
            otherNotificationsOpen: false,

            notificationCounts: null,
            requestInterval: null,
        }
    },

    computed: {
        notificationTypes() {
            const types = [
                { val: "sust_links", text: i18n.t('notifications.link'), icon: "fi fi-ss-city" },
                { val: "system", text: i18n.t('notifications.system'), icon: "fi fi-ss-wind-turbine" },
                { val: "other", text: i18n.t('notifications.other'), icon: "fi fi-ss-bell" },
            ];
            return types;
        },

        isNotificationsButtonsActive() {
            const active = (this.linkNotificationsOpen || this.systemNotificationsOpen || this.otherNotificationsOpen);
            return active;
        },

        totalNotificationCount() {
            if (!this.notificationCounts) return 0;

            let total = 0;
            for (const key in this.notificationCounts) {
                if (!Object.hasOwn(this.notificationCounts, key)) continue;
                total += Number(this.notificationCounts[key]);
            }

            return total;
        }
    },

    mounted() {
        this._getNotificationCounts();
        this.requestInterval = setInterval(() => {
            this._getNotificationCounts();
        }, 10000);
    },

    beforeDestroy() {
        clearInterval(this.requestInterval);
    },

    methods: {
        ...mapActions('app', [
            'getNotificationCounts'
        ]),

        _getNotificationCounts() {
            this.getNotificationCounts()
                .then(res => {
                    this.notificationCounts = res.data;
                })
        },

        // handlers //

        async handleClickNotifications(bool) {
            await this.setNotificationsOpen(bool);
        },

        async handleClickNotificationButton(item) {
            if (!item) {
                await this.resetNotificationButtons();
                await this.setNotificationsOpen(true);
                await this.setShowUnread(true);
            } else if (item.val === "sust_links") {
                await this.setNotificationsOpen(false);
                await this.setSystemNotificationsOpen(false);
                await this.setOtherNotificationsOpen(false);
                await this.setLinkNotificationsOpen(true);
            } else if (item.val === "system") {
                await this.setNotificationsOpen(false);
                await this.setSystemNotificationsOpen(true);
                await this.setOtherNotificationsOpen(false);
                await this.setLinkNotificationsOpen(false);
            } else if (item.val === "other") {
                await this.setNotificationsOpen(false);
                await this.setSystemNotificationsOpen(false);
                await this.setOtherNotificationsOpen(true);
                await this.setLinkNotificationsOpen(false);
            } else {
                console.error("Invalid item on handleClickNotificationButton: ", item);
            }
        },

        // setters //

        async setNotificationsOpen(bool) {
            this.notificationsOpen = bool;
        },

        async setLinkNotificationsOpen(bool) {
            this.linkNotificationsOpen = bool;
        },

        async setSystemNotificationsOpen(bool) {
            this.systemNotificationsOpen = bool;
        },

        async setOtherNotificationsOpen(bool) {
            this.otherNotificationsOpen = bool;
        },

        async setShowUnread(bool) {
            this.showUnread = bool;
        },

        async resetNotificationButtons() {
            await this.setSystemNotificationsOpen(false);
            await this.setOtherNotificationsOpen(false);
            await this.setLinkNotificationsOpen(false);
        },

        async resetEverything() {
            await this.setSystemNotificationsOpen(false);
            await this.setOtherNotificationsOpen(false);
            await this.setLinkNotificationsOpen(false);
            await this.setNotificationsOpen(false);
            await this.setShowUnread(true);
        },

        // emitter //

        handleComponentClick(e) {
            this.$emit("click", e);
        },

        handleCountChange(e, type) {
            this.notificationCounts[type] += e;
        },
    }
}
</script>

<template>
    <div class="notifications-button-container" tabindex="0" @click="handleComponentClick()">
        <notification-count v-if="totalNotificationCount > 0" :count="totalNotificationCount" class="notifications-count" />
      
        <i v-if="!notificationsOpen && !isNotificationsButtonsActive" class="notifications-button fa-solid fa-envelope" @click="handleClickNotifications(true)"></i>
        <i v-else class="notifications-button fa-solid fa-envelope-open" @click="resetEverything()"></i>
        
        <Transition name="fade">
            <div class="notification-buttons-container" v-if="notificationsOpen">
                <div v-for="item in notificationTypes" :key="item.val" class="notification-type-button"
                    @click="handleClickNotificationButton(item)"
                >
                    <notification-count v-if="notificationCounts[item.val] > 0" :count="notificationCounts[item.val]" class="notifications-count" />
                    <div class="button-inner">
                        <i :class="item.icon"></i>
                        <span>{{ item.text }}</span>
                    </div>
                </div>
            </div>
        </Transition>
        
        <div class="notifications-parent-container">
            <sust-links v-if="linkNotificationsOpen" @count="handleCountChange($event, 'sust_links')" />
            <other v-if="otherNotificationsOpen" @count="handleCountChange($event, 'other')" />
        </div>
    </div>
</template>

<style lang='scss' scoped>
    .fade-active {
        transition: opacity .66s ease;
    }

    .fade-from,
    .fade-to {
        opacity: 1;
    }
    
    .notifications-button-container {
        margin-right: 1em;
        z-index: 999;
        position: relative;

        > .notifications-count {
            position: absolute;
            bottom: 0;
            right: 0;
            transform: translateX(50%);
        }
    }

    .notifications-button {
        padding-top: .15em;
        font-size: 2em;
        color: var(--sustainfinity-navy--hover);
            
        transition: all 150ms;
        cursor: pointer;
        &:hover {
            color: var(--sustainfinity-navy);
        }
    }

    .notification-buttons-container {
        position: absolute;
        transform: translateY(105%);
        bottom: 0px;
        right: 0;

        background-color: whitesmoke;
        border-radius: 1em;
        border: thin solid var(--sustainfinity-navy);

        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-content: center;
        flex-wrap: wrap;

        padding: .5em;
        width: 200px;

        > .notification-type-button {
            width: 50%;
            min-height: 70px;
            max-height: 70px;
            border-radius: 7px;

            display: flex;
            flex-direction: row;
            justify-content: center;
            align-content: center;
            align-items: center;

            cursor: pointer;
            &:hover {
                background-color: lightgray;
            }

            position: relative;
            > .notifications-count {
                position: absolute;
                top: 7.5%;
                right: 7.5%;
            }

            > .button-inner {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-content: center;
                align-items: center;
                text-align: center;
                color: black;

                > span {
                    font-weight: bold;
                    font-style: italic;
                    font-size: .9em;
                }
            }
        }
    }

    .notifications-parent-container {
        position: absolute;
        top: 100%;
        right: 0;
    }

    .back-btn-container {
        padding: .5em;
        width: 100%;
        border-bottom: thin solid lightgray;
        > i {
            color: var(--sustainfinity-navy);
            transition: all 150ms;
            cursor: pointer;
            &:hover {
                color: var(--sustainfinity-navy--hover);
            }
        }
    }

</style>